import { useContext, useEffect, useRef, useState } from 'react';
import { SmartContext } from '../Core/SmartContext';
import { evaluateExpression, getControlValueFromState, handleControlValueChange, isEmpty } from '../Core/SmartFunctions';
import { SimpleFormControlArguments, State } from '../Core/SmartTypes';
import logger from '../Core/logger';
import { axiosClient } from '../Service/axiosClient';
import ErrorControl from './ErrorControl';
import Swal from 'sweetalert2';

const SmartFileUploader = (args: SimpleFormControlArguments) => {
    const { state, dispatch } = useContext(SmartContext);
    const { control, dataKey, parentDataKey } = { ...args };
    const data = getControlValueFromState(dataKey, state as State);
    const isHidden = evaluateExpression(control.hideExpression, state?.data);
    const customProperties = control?.props?.customProperties;
    const [invalidFile, setInvalidFile] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [imagelink, setImageLink] = useState('');
    const [doclink, setDocLink] = useState('');
    const imgArray = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'];
    const validExtensions = customProperties?.validExtensions ?? [...imgArray, 'pdf', 'doc', 'docx'];
    const maxFileSizeMB = customProperties?.maxFileSizeMB || 50; // Default max file size is 50MB
    const isMultiple = customProperties?.multiple === true || false;  //Default isMultiple false

    // Ref for file input
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    // For multiple mode: state stores an array; support legacy comma-separated string too
    const currentFiles: string[] = isMultiple && data
        ? (Array.isArray(data)
            ? data
            : String(data).split(',').map((f: string) => f.trim()).filter(Boolean))
        : [];

    // Always keep a ref to the latest files so async upload closures never read stale state
    const currentFilesRef = useRef<string[]>(currentFiles);
    currentFilesRef.current = currentFiles;

    // For single mode: set image/doc link from state data
    if (!isMultiple && data && !imagelink && !doclink) {
        const extension = data.split('.').at(-1);
        if (imgArray.includes(extension.toLowerCase())) {
            setImageLink(data);
            setDocLink('');
        } else {
            setDocLink(data);
            setImageLink('');
        }
    }

    useEffect(() => {
        if (invalidFile) {
            const timer = setTimeout(() => {
                setInvalidFile(false);
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [invalidFile]);

    const uploadFile = async (file: File): Promise<string | null> => {
        const extension = file?.name.split('.').at(-1)?.toLowerCase();

        if (!extension || !validExtensions.includes(extension)) {
            setInvalidFile(true);
            return null;
        }
        const fileSizeMB = file.size / (1024 * 1024);
        if (fileSizeMB > maxFileSizeMB) {
            Swal.fire({
                icon: 'error',
                title: 'Oops...!',
                text: `File size exceeds the maximum limit of ${maxFileSizeMB} MB.`,
            });
            return null;
        }

        const formData = new FormData();
        formData.append('pdf_doc', file);

        try {
            const response = await axiosClient().post(`${process.env.REACT_APP_COMMON_API}/upload/get-attached`, formData);
            if (response.status === 400) {
                alert(response.data.message);
                return null;
            }
            return response.data.link as string;
        } catch (error) {
            console.error('An error occurred:', error);
            alert('Error uploading file');
            return null;
        }
    };

    const handleFileControlUpload = async (files: FileList) => {
        logger.log(state?.data);
        setUploading(true);

        if (isMultiple) {
            const newLinks = (await Promise.all(Array.from(files).map(uploadFile))).filter(
                (l): l is string => l !== null
            );
            if (newLinks.length > 0) {
                // Use ref so we always merge with the latest list, never a stale closure value
                const merged = [...currentFilesRef.current, ...newLinks];
                handleControlValueChange({ control, value: merged, dataKey, parentDataKey, state: state as State, dispatch });
            }
        } else {
            const link = await uploadFile(files[0]);
            if (link) {
                if (imgArray.includes(link.split('.').at(-1)!.toLowerCase())) {
                    setImageLink(link);
                    setDocLink('');
                } else {
                    setDocLink(link);
                    setImageLink('');
                }
                handleControlValueChange({ control, value: link, dataKey, parentDataKey, state: state as State, dispatch });
            }
        }

        setUploading(false);
    };

    const handleRemoveFile = (fileUrl: string) => {
        const updated = currentFilesRef.current.filter((f) => f !== fileUrl);
        handleControlValueChange({ control, value: updated.length ? updated : null, dataKey, parentDataKey, state: state as State, dispatch });
    };

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setDragOver(false);
        if (event.dataTransfer.files?.length) {
            handleFileControlUpload(event.dataTransfer.files);
        }
    };

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setDragOver(true);
        event.dataTransfer.dropEffect = 'copy';
    };

    const handleDragLeave = () => setDragOver(false);

    const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files.length > 0) {
            handleFileControlUpload(event.target.files);
            event.target.value = '';
        }
    };

    const handleBrowseClick = () => fileInputRef.current?.click();

    const getFileName = (url: string) => decodeURIComponent(url.split('/').at(-1) ?? url);

    const isImageUrl = (url: string) => imgArray.includes(url.split('.').at(-1)?.toLowerCase() ?? '');

    if (isHidden) return <></>;

    return (
        <div className="w-100">
            <label htmlFor="formFileLg" className="form-label">
                {control.props.label}
            </label>

            <div
                className={`upload_section ${dragOver ? 'drag-over' : ''}`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
            >
                <input
                    ref={fileInputRef}
                    id={control.id}
                    data-testid={control.id}
                    type="file"
                    hidden={control.props?.isCode || isHidden}
                    className="form-control form-control-lg pl-0 d-none"
                    accept=".jpg, .jpeg, .png, .gif, .bmp, .webp, .pdf, .doc, .docx, .mp3"
                    name="cover"
                    multiple={isMultiple}
                    onChange={handleFileInputChange}
                />
                <div className="row g-2 align-items-center justify-content-center">
                    <div className="col-sm d-flex align-items-center justify-content-center">
                        <div className="cloud-wrap px-3 px-sm-5 mx-auto">
                            <div className="cloud-img">
                                <img src={`${process.env.REACT_APP_IMAGE_BASEPATH}/upload.svg`} alt="" />
                            </div>
                            <div className="cloud-text">
                                {uploading ? (
                                    <span>Uploading...</span>
                                ) : dragOver ? (
                                    <span>Drop here</span>
                                ) : (
                                    <span>
                                        Drag and drop file{isMultiple ? 's' : ''} here or{' '}
                                        <u style={{ cursor: 'pointer' }} onClick={handleBrowseClick} className="text-sky-blue">Browse</u>
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="text-end font-14 d-block text-dark-blue opacity-7">
                <span className={`d-inline-block ${invalidFile ? 'shake-err' : ''}`}>Only {validExtensions.join(', ')} files.&nbsp;</span>
                {!isEmpty(customProperties?.infoLine) && (
                    <span>{customProperties?.infoLine}</span>
                )}
            </div>

            <ErrorControl errorMessages={state?.formValidationErrors[dataKey]} />

            {/* Single mode previews */}
            {!isMultiple && imagelink && <img src={imagelink} width="160" alt="" className="img-fluid" />}
            {!isMultiple && doclink && (
                <div className="d-flex align-items-center gap-2 mt-1">
                    <a href={doclink} target="_blank" rel="noopener noreferrer">View File</a>
                    <button
                        type="button"
                        title="Remove"
                        onClick={() => {
                            setDocLink('');
                            setImageLink('');
                            handleControlValueChange({ control, value: null, dataKey, parentDataKey, state: state as State, dispatch });
                        }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#e74c3c', fontWeight: 'bold', fontSize: '16px', lineHeight: 1, padding: '0 2px' }}
                    >
                        &times;
                    </button>
                </div>
            )}

            {/* Multiple mode previews */}
            {isMultiple && currentFiles.length > 0 && (
                <div className="mt-2 d-flex flex-wrap gap-2">
                    {currentFiles.map((fileUrl, index) => (
                        <div
                            key={fileUrl}
                            className="d-flex align-items-center border rounded px-2 py-1"
                            style={{ gap: '8px' }}
                        >
                            <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="font-13">
                                View File {index + 1}
                            </a>
                            <button
                                type="button"
                                title="Remove"
                                onClick={() => handleRemoveFile(fileUrl)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#e74c3c', fontWeight: 'bold', fontSize: '16px', lineHeight: 1, padding: '0 2px' }}
                            >
                                &times;
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default SmartFileUploader;
